/**
 * The explicit states a list can be in. Kept together so Queue and History
 * cannot drift into telling the operator different stories about the same
 * situation — and so the two EMPTY messages stay deliberately different: the
 * queue is scope-bounded work, history is a period search.
 */
import { useTranslation } from "react-i18next";
import { AlertTriangle, Inbox, Loader2, RefreshCw, SearchX } from "lucide-react";
import { Button } from "@/ui/components/ui/button";
import { cn } from "@/ui/lib/utils";
import { Skeleton } from "@/ui/components/ui/skeleton";
import { AlertApiError } from "@/alerts/api/envelope";

export const AlertListSkeleton = () => {
  const { t } = useTranslation("alerts");
  return (
  <ul className="divide-y divide-border" aria-busy="true" aria-label={t("list.loading")}>
    {Array.from({ length: 6 }, (_, i) => (
      <li key={i} className="flex items-center gap-3 px-4 py-3">
        <Skeleton className="h-9 w-9 rounded-md" />
        <div className="flex-1 space-y-2">
          <Skeleton className="h-3.5 w-48" />
          <Skeleton className="h-3 w-72" />
        </div>
        <Skeleton className="h-3 w-16" />
      </li>
    ))}
  </ul>
  );
};

const EmptyShell = ({
  icon: Icon,
  title,
  hint,
}: {
  icon: typeof Inbox;
  title: string;
  hint?: string;
}) => (
  <div className="flex flex-col items-center justify-center gap-2 px-6 py-16 text-center">
    <Icon className="h-8 w-8 text-muted-foreground/60" />
    <p className="text-sm font-medium">{title}</p>
    {hint && <p className="max-w-sm text-xs text-muted-foreground">{hint}</p>}
  </div>
);

/** Queue empty — never mentions a date range; the queue has no time bounds. */
export const QueueEmptyState = () => {
  const { t } = useTranslation("alerts");
  return (
    <EmptyShell
      icon={Inbox}
      title={t("list.queueEmpty.title")}
      hint={t("list.queueEmpty.hint")}
    />
  );
};

export const HistoryEmptyState = () => {
  const { t } = useTranslation("alerts");
  return (
    <EmptyShell
      icon={SearchX}
      title={t("list.historyEmpty.title")}
      hint={t("list.historyEmpty.hint")}
    />
  );
};

/** Prompt shown before a scope exists — the queue contract requires a location. */
export const ScopeRequiredState = ({ what }: { what: "queue" | "history" }) => {
  const { t } = useTranslation("alerts");
  return <EmptyShell icon={Inbox} title={t(`list.scopeRequired.${what}`)} />;
};

export const AlertsErrorState = ({
  error,
  onRetry,
}: {
  error: unknown;
  onRetry: () => void;
}) => {
  const { t } = useTranslation("alerts");
  const apiError = error instanceof AlertApiError ? error : null;
  const rateLimited = apiError?.isRateLimited ?? false;
  return (
    <div className="flex flex-col items-center justify-center gap-3 px-6 py-16 text-center">
      <AlertTriangle className="h-8 w-8 text-destructive/70" />
      <div className="space-y-1">
        <p className="text-sm font-medium">{t("list.error.title")}</p>
        <p className="max-w-md text-xs text-muted-foreground">
          {apiError?.message ?? t("list.error.generic")}
          {rateLimited && apiError?.retryAfterSeconds != null
            ? ` ${t("list.error.retryIn", { seconds: apiError.retryAfterSeconds })}`
            : ""}
        </p>
      </div>
      <Button size="sm" variant="outline" onClick={onRetry} className="gap-2">
        <RefreshCw className="h-3.5 w-3.5" />
        {t("list.error.retry")}
      </Button>
    </div>
  );
};

/**
 * Explicit refresh. The Queue polls and History reloads on any filter change,
 * but an operator who has just actioned an alert elsewhere wants to force the
 * question rather than wait out a poll interval.
 */
export const RefreshButton = ({
  isFetching,
  onRefresh,
}: {
  isFetching: boolean;
  onRefresh: () => void;
}) => {
  const { t } = useTranslation("alerts");
  return (
    <Button
      size="sm"
      variant="ghost"
      className="h-8 gap-1.5 px-2 text-xs"
      disabled={isFetching}
      onClick={onRefresh}
    >
      <RefreshCw className={cn("h-3.5 w-3.5", isFetching && "animate-spin")} />
      {t("list.refresh")}
    </Button>
  );
};

/**
 * Non-destructive refresh indicator. A poll or a live-hint re-read must never
 * replace the table an operator is working in — this sits in the header instead.
 */
export const RefreshingChip = ({ show }: { show: boolean }) => {
  const { t } = useTranslation("alerts");
  return show ? (
    <span className="flex items-center gap-1.5 text-[11px] text-muted-foreground">
      <Loader2 className="h-3 w-3 animate-spin" />
      {t("list.refreshing")}
    </span>
  ) : null;
};

/**
 * The backend limited the result set. Silence here would present a partial
 * answer as the complete one.
 */
export const TruncatedNotice = ({ show }: { show: boolean }) => {
  const { t } = useTranslation("alerts");
  return show ? (
    <div className="flex items-start gap-2 border-b border-amber-500/30 bg-amber-500/10 px-4 py-2 text-xs text-amber-700 dark:text-amber-400">
      <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
      <span>{t("list.truncated")}</span>
    </div>
  ) : null;
};
